import { FormControl, InputAdornment, Typography } from "@mui/material";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import React from "react";
import styled from "styled-components";
import { useApi } from "../../contexts/ApiContext";
import FormLabel from "../FormLabel";
import { InputProps } from "./InputProps";

const StyledTextField = styled(TextField)`
  margin: 0px 0;
  padding: 0;
`;

export type TextInputPropsType = InputProps & TextFieldProps;

type TextInputTypeProp = "text" | "number" | "float" | "integer" | "password";

const numberTypes = ["number", "float", "integer"];

interface TextInputInputProps<T extends TextInputTypeProp>
  extends Omit<TextInputPropsType, "onChange"> {
  onChange: (value: T extends "number" ? number : string) => void;
  adornment?: string;
  type?: TextInputTypeProp;
  unit?: string;
  allowNegative?: boolean;
}

const TextInput = <T extends "text" | "number" | "password">({
  label,
  value,
  onChange,
  error,
  helperText,
  adornment,
  isRequired = true,
  rows = 3,
  maxRows = 10,
  type = "text",
  allowNegative = false,
  ...rest
}: TextInputInputProps<T>) => {
  const { isSubmitting, isLoading } = useApi();
  const isDisabled = isLoading || isSubmitting;

  return (
    <FormControl disabled={isDisabled} fullWidth>
      <FormLabel error={error}>{label}</FormLabel>

      <StyledTextField
        disabled={isDisabled}
        {...rest}
        InputProps={
          adornment
            ? {
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography color={"greyButton"}>{adornment}</Typography>
                  </InputAdornment>
                ),
              }
            : {}
        }
        rows={rows}
        maxRows={maxRows}
        variant="outlined"
        fullWidth
        type={numberTypes.includes(type) ? "number" : type}
        value={value ?? ""}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          let val = event.target.value;
          switch (type) {
            case "number":
            case "float":
            case "integer":
              let num = event.target.valueAsNumber;
              if (val === "") {
                onChange(null as any);
              } else if (
                !isNaN(num) &&
                (allowNegative || num >= 0) &&
                (type !== "integer" || Number.isInteger(num))
              ) {
                onChange(num as any);
              }
              return;
          }
          onChange(val as any);
        }}
        error={error}
        helperText={error ? helperText : " "}
      />
    </FormControl>
  );
};

export default TextInput;
